import React, { useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import './FilterDrawer.scss'
import { actionCreators as GlobalStoreCreator } from "../../store/General"
import * as debounce from '../../utils/debounce'
import { actionCreators as filterCreator } from '../../store/Filter'

const {
  selectFilter,
  deselectFilter,
  deselectFilterGroup,
  expandFilterGroup,
  collapseFilterGroup,
  expandOnlySearchedFilterGroups,
  collapseAllFilterGroups,
  deselectAll,
  setUseCustomLabel,
  setCustomLabel,
  setActiveFilterCategory,
} = filterCreator



export const FilterDrawer = (props) => {
  const {
    isOpen,
    closeRequested,
    datasetId,
    custom,
    isLoading,
    selectedFilter = {},
    hiddenCategories = [], // optional: array of category names to hide (e.g. ['Fleet'] or ['Consumer'])
  } = props

  const dispatch = useDispatch()

  const filterState = useSelector((state) => state.filter)
  const { filterGroups = [] } = filterState

  const [selectedCategory, setSelectedCategory] = useState('General')
  const [searchTerm, setSearchTerm] = useState('')
  const [isSearching, setIsSearching] = useState(false)

  const searchInputRef = useRef(null)

  const iconCollapsed  = 'fal fa-folder'
  const iconCollapsed2 = 'fa fa-folder'
  const iconExpanded   = 'fal fa-folder-open'
  const iconExpanded2  = 'fa fa-folder-open'
  const selIndicator   = '●'

  useEffect(() => {
    const hiddenSet = new Set((hiddenCategories || []).map(c => String(c).trim()))
    const visibleFilterGroups = filterGroups
      .filter(fg => fg.showOnUi !== false)
      .filter(fg => !hiddenSet.has(String(fg.category || 'General').trim()))
    const visibleCategories = [...new Set(visibleFilterGroups.map(fg => fg.category))]

    let defaultCat = 'General'
    if (visibleCategories.length > 0) {
      defaultCat = visibleCategories[0]
    }
    if (custom && visibleCategories.includes(custom)) {
      defaultCat = custom
    }

    setSelectedCategory(defaultCat)

    // Optional logs – remove in production
    console.log('visibleCategories', visibleCategories)
    console.log('defaultCategory', defaultCat)
    console.log('props.custom', custom)
  }, [filterGroups, custom, hiddenCategories])

  const beginSearch = useRef(
    debounce.debounce((term) => {
      dispatch(expandOnlySearchedFilterGroups(term))
    }, debounce.forecastDelay)
  ).current

  const handleCategoryClick = (cat) => {
    setSelectedCategory(cat)
    dispatch(setActiveFilterCategory(cat))
  }

  const getNbFiltersSelected = (filterGroup) => {
    let count = 0
    for (const f of Object.values(filterGroup.filters || {})) {
      if (f.isSelected) count++
    }
    return count
  }

  const clearFilterGroupSelections = (filterGroup) => {
    dispatch(deselectFilterGroup(filterGroup.id, datasetId))
  }

  const handleFilterClick = (fgId, filterId, dsId, isSelected) => {
    if (isSelected) {
      dispatch(deselectFilter(fgId, filterId, dsId))
    } else {
      dispatch(selectFilter(fgId, filterId, null, dsId))
    }
    dispatch(GlobalStoreCreator.setGlobalStore('forecast_needed', true))
  }

  let displayedFilterGroups = filterGroups
  if (datasetId) {
    displayedFilterGroups = filterGroups.filter(fg => fg.datasetId === datasetId)
  }

  const categories = []
  const defaultCatForFallback = displayedFilterGroups.length > 0 ? displayedFilterGroups[0].category : 'General'
  const hiddenSet = new Set((hiddenCategories || []).map(c => String(c).trim()))

  displayedFilterGroups
    .filter(fg => fg.showOnUi !== false)
    .filter(fg => !hiddenSet.has(String(fg.category || defaultCatForFallback).trim()))
    .forEach(fg => {
      const cat = fg.category || defaultCatForFallback
      if (!categories.includes(cat)) {
        categories.push(cat)
      }
    })

  const categoryCounts = {}
  const displayedFilterGroupsNoHidden = displayedFilterGroups.filter(
    fg => !hiddenSet.has(String(fg.category || defaultCatForFallback).trim())
  )
  displayedFilterGroupsNoHidden.forEach(fg => {
    const cat = fg.category || defaultCatForFallback
    const nb = getNbFiltersSelected(fg)
    categoryCounts[cat] = (categoryCounts[cat] || 0) + nb
  })

  let selFilters = isSearching
    ? displayedFilterGroupsNoHidden.filter(fg => fg.showOnUi)
    : displayedFilterGroupsNoHidden.filter(
        fg => (fg.category || defaultCatForFallback) === (selectedCategory || defaultCatForFallback) && fg.showOnUi
      )

  const elements = isLoading ? (
    <div>loading...</div>
  ) : !displayedFilterGroups.length ? (
    <div>error</div>
  ) : (
    selFilters.map(fg => {
      const nb = getNbFiltersSelected(fg)

      const groupHeader = (
        <div
          className={`group-item${nb > 0 ? ' has-selections' : ''}`}
          onClick={() =>
            dispatch(
              fg.isExpanded ? collapseFilterGroup(fg.id) : expandFilterGroup(fg.id)
            )
          }
        >
          <i
            className={
              fg.isExpanded
                ? nb > 0 ? iconExpanded2 : iconExpanded
                : nb > 0 ? iconCollapsed2 : iconCollapsed
            }
          />
          &nbsp;{fg.label}
          {nb > 0 && <span className="sel-indicator"> {selIndicator}</span>}
          {nb > 0 && (
            <button
              className="clear-selections"
              title="Clear selection(s)"
              aria-label="clear selections"
              onClick={ev => {
                clearFilterGroupSelections(fg)
                ev.preventDefault()
                ev.stopPropagation()
              }}
            >
              <i className="fal fa-times" />
            </button>
          )}
        </div>
      )

      return (
        <div key={`fg_${fg.id}`} className="filter-group">
          {isSearching ? (fg.matchedGroup ? groupHeader : null) : groupHeader}

          {fg.isExpanded &&
            fg.filters.map(f => {
              const item = (
                <div
                  key={`filter_${f.id || f.syntax}`}
                  className={`filter-item${f.isSelected ? ' selected' : ''}`}
                  onClick={() => handleFilterClick(fg.id, f.id || f.syntax, datasetId, f.isSelected)}
                >
                  {f.label}
                </div>
              )

              return isSearching ? (f.matched ? item : null) : item
            })}
        </div>
      )
    })
  )

  return (
    <div className={`filter-drawer${isOpen ? ' open' : ''}`}>

      <div className="pane-title">
        <i className="fal fa-users" /> Filter

        <button
          style={{ float: 'right', cursor: 'pointer' }}
          aria-label="close"
          onClick={closeRequested}
        >
          <i className="fal fa-times" />
        </button>

        <button
          className="right clear-all-btn"
          aria-label="clear filter"
          onClick={() => {
            dispatch(setUseCustomLabel(false))
            dispatch(deselectAll(datasetId))
          }}
        >
          <i className="fal fa-eraser" /> clear filter
        </button>
      </div>

      <div className="section filter-label">
        <div className="title">Label ({selectedFilter.useCustomLabel ? 'custom' : 'auto'})</div>
        <div className="row">
          <button
            className={`toggle-btn${selectedFilter.useCustomLabel ? ' selected' : ''}`}
            aria-label="customize label"
            title="Customize label"
            onClick={() => dispatch(setUseCustomLabel(!selectedFilter.useCustomLabel))}
          >
            <i className="fal fa-edit" />
          </button>

          {selectedFilter.useCustomLabel ? (
            <input
              type="text"
              placeholder="custom filter label"
              aria-label="custom filter label"
              value={selectedFilter.customLabel || ''}
              onChange={e => dispatch(setCustomLabel(e.target.value))}
            />
          ) : (
            <div className="auto-label" title={selectedFilter.autoLabel}>
              {selectedFilter.autoLabel}
            </div>
          )}
        </div>
      </div>

      <div className="section bottom-border subgroups-head">
        <div className="title">Subgroup(s)</div>

        <div className="filter-search">
          <input
            ref={searchInputRef}
            type="text"
            placeholder="search"
            onChange={e => {
              const val = e.target.value.trim()
              setSearchTerm(val)
              if (val) {
                setIsSearching(true)
                beginSearch(val)
              } else {
                setIsSearching(false)
                dispatch(collapseAllFilterGroups())
              }
            }}
          />
          <button
            className="clear"
            aria-label="clear search"
            title="Clear search"
            onClick={() => {
              if (searchInputRef.current) searchInputRef.current.value = ''
              setSearchTerm('')
              setIsSearching(false)
              dispatch(collapseAllFilterGroups())
            }}
          >
            <i className="fal fa-times" />
          </button>
        </div>

        {!isSearching && (
          <div className="tab-header">
            {categories.map(cat => (
              <div
                key={`tab_${cat}`}
                className={`tab${selectedCategory === cat ? ' selected' : ''}${categoryCounts[cat] > 0 ? ' has-selections' : ''}`}
                onClick={() => handleCategoryClick(cat)}
              >
                {cat}
                {categoryCounts[cat] > 0 && (
                  <span className="sel-indicator"> {selIndicator}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="scroller">{elements}</div>
    </div>
  )
}